import { Link } from "@tanstack/react-router";
import { FileTextIcon, ArrowLeftIcon, MailIcon, HeadphonesIcon } from "./LegalIcons.tsx";

export function TermsOfServicePage() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-app, #0f172a)",
        color: "var(--text-1, #f8fafc)",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        padding: "40px 20px 80px",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {/* Header Bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 32,
            borderBottom: "1px solid var(--border-soft, rgba(255,255,255,0.08))",
            paddingBottom: 20,
          }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "var(--text-2, #94a3b8)",
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <ArrowLeftIcon size={16} /> Back to SellSynq
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                color: "#fff",
                fontSize: 14,
              }}
            >
              S
            </div>
            <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: "-0.02em" }}>SellSynq</span>
          </div>
        </div>

        {/* Hero Section */}
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(16, 185, 129, 0.12)",
              color: "#34d399",
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 14,
            }}
          >
            <FileTextIcon size={14} /> Official Terms of Service &amp; Support Policy
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 800,
              margin: "0 0 12px",
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
            }}
          >
            Terms of Service &amp; Customer Support
          </h1>
          <p style={{ color: "var(--text-3, #64748b)", fontSize: 13, margin: 0 }}>
            Last Updated: October 26, 2026 • Effective Date: October 26, 2026 • Domain: sellsynq.com
          </p>
        </div>

        {/* Legal Body */}
        <div
          style={{
            background: "var(--surface, rgba(30, 41, 59, 0.7))",
            border: "1px solid var(--border-soft, rgba(255,255,255,0.08))",
            borderRadius: 16,
            padding: "36px 32px",
            lineHeight: 1.7,
            fontSize: 14.5,
            color: "var(--text-2, #cbd5e1)",
          }}
        >
          {/* Section 1 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              1. Acceptance of Terms
            </h2>
            <p>
              These Terms of Service ("Terms") govern your access to and use of <strong>SellSynq</strong> (accessible at{" "}
              <a href="https://sellsynq.com" style={{ color: "#818cf8" }}>https://sellsynq.com</a>), including our web
              application, AI meeting assistant, integrations, and customer support. By creating an account or accessing the
              platform, you agree to be bound by these Terms and our Privacy Policy.
            </p>
          </section>

          {/* Section 2 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              2. Description of the Service
            </h2>
            <p>
              SellSynq provides an enterprise AI copilot for commercial teams. Key features include:
            </p>
            <ul style={{ paddingLeft: 20 }}>
              <li>Real-time speech-to-text transcription and live speaker sentiment analysis during sales conversations.</li>
              <li>Dynamic AI coaching suggestions and objection handling grounded in company SOPs and playbooks.</li>
              <li>Automated meeting recaps, follow-up items, and executive summaries.</li>
              <li>Integrations with Google Calendar, Google Drive, Monday.com, and Microsoft SharePoint.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              3. User Accounts &amp; Multi-Tenant Subdomains
            </h2>
            <p>
              Organizations can establish dedicated subdomains (e.g., <code>[company].sellsynq.com</code>). The creator of
              an organization account acts as the primary Administrator and is responsible for managing member access,
              configuring knowledge documents, and maintaining credentials. You agree to notify us immediately of any
              unauthorized use of your account.
            </p>
          </section>

          {/* Section 4 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              4. Third-Party Integrations &amp; Permissions
            </h2>
            <p>
              When connecting third-party platforms (including Google Workspace, Monday.com, and Microsoft 365), you grant
              SellSynq permission to access and synchronize relevant files and events solely for your organization's use.
              SellSynq strictly adheres to Google API User Data Policies and does not use third-party data for unauthorized
              commercial purposes.
            </p>
          </section>

          {/* Section 5 - Customer Support */}
          <section
            style={{
              marginBottom: 32,
              background: "rgba(16, 185, 129, 0.05)",
              border: "1px solid rgba(16, 185, 129, 0.2)",
              borderRadius: 12,
              padding: 20,
            }}
          >
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#6ee7b7",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <HeadphonesIcon size={18} color="#34d399" /> 5. Customer Support &amp; SLA
            </h2>
            <p style={{ margin: "0 0 12px" }}>
              We are dedicated to providing responsive, enterprise-grade support to all SellSynq customers:
            </p>
            <ul style={{ paddingLeft: 20, margin: "0 0 14px" }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Technical Support Inquiries:</strong> Direct email assistance is available at{" "}
                <a href="mailto:support@sellsynq.com" style={{ color: "#6ee7b7", fontWeight: 600 }}>
                  support@sellsynq.com
                </a>{" "}
                and{" "}
                <a href="mailto:moshe@echotora.com" style={{ color: "#6ee7b7", fontWeight: 600 }}>
                  moshe@echotora.com
                </a>
                .
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>Response Time:</strong> Our standard support response window is within <strong>24 business hours</strong>{" "}
                for standard inquiries, and within <strong>4 hours</strong> for critical system interruptions.
              </li>
              <li>
                <strong>Onboarding &amp; Setup Assistance:</strong> Dedicated onboarding sessions and custom connector setup
                guidance are provided for enterprise teams.
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              6. Intellectual Property &amp; Customer Data
            </h2>
            <p>
              You retain all ownership, intellectual property rights, and control over your uploaded documents, recordings,
              and sales collateral. SellSynq retains all rights, title, and interest in and to the SellSynq software,
              proprietary models, and trademarks.
            </p>
          </section>

          {/* Section 7 */}
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              7. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by applicable law, SellSynq provides the service "as is" and "as available".
              In no event shall SellSynq or its affiliates be liable for any indirect, incidental, special, consequential, or
              punitive damages arising from your use of the service.
            </p>
          </section>

          {/* Section 8 */}
          <section style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1, #f8fafc)", marginBottom: 12 }}>
              8. Contact Support
            </h2>
            <p>
              For support inquiries, billing questions, or legal notices, please contact us:
            </p>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 8,
                background: "var(--surface-hover, rgba(255,255,255,0.06))",
                color: "#e2e8f0",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <MailIcon size={16} color="#34d399" /> support@sellsynq.com • moshe@echotora.com
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div
          style={{
            marginTop: 32,
            textAlign: "center",
            fontSize: 13,
            color: "var(--text-3, #64748b)",
            display: "flex",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <Link to="/pa" style={{ color: "var(--text-3, #64748b)", textDecoration: "none" }}>
            Application Privacy Policy
          </Link>
          <span>•</span>
          <a href="https://sellsynq.com" style={{ color: "var(--text-3, #64748b)", textDecoration: "none" }}>
            SellSynq Homepage
          </a>
        </div>
      </div>
    </div>
  );
}
